import { AxiosError } from 'axios';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import * as Api from '../../api';
import { TApiFolder } from '../../api/endpoints/Folder';
import {
  TApiOrder,
  TApiOrderDetails,
  TOrderCreateParams,
  TOrderSubmitParams,
  TOrderUpdateParams
} from '../../api/endpoints/Order';
import { TRootState } from '../reducers/RootReducer';
import { Nullable } from "../../../types/utils";
import ErrorHelpers from "../../utils/ErrorHelpers";

export const ORDERS_FETCH_STARTED = 'ORDERS_FETCH_STARTED';
export const ORDERS_FETCH_COMPLETED = 'ORDERS_FETCH_COMPLETED';
export const ORDERS_FETCH_FAILED = 'ORDERS_FETCH_FAILED';

export interface IFetchOrdersStartedAction {
  type: typeof ORDERS_FETCH_STARTED;
  folderId: TApiFolder['id'] | null;
}

const fetchOrdersStarted = (folderId?: IFetchOrdersStartedAction['folderId']): IFetchOrdersStartedAction => ({
  type: ORDERS_FETCH_STARTED,
  folderId: folderId ?? null
});

export interface IFetchOrdersCompletedAction {
  type: typeof ORDERS_FETCH_COMPLETED,
  data: Array<TApiOrder>,
  folderId: TApiFolder['id'] | null;
}

const fetchOrdersCompleted = (
  data: Array<TApiOrder>,
  folderId?: IFetchOrdersCompletedAction['folderId']
): IFetchOrdersCompletedAction => ({
  type: ORDERS_FETCH_COMPLETED,
  data,
  folderId: folderId ?? null
});

export interface IFetchOrdersFailedAction {
  type: typeof ORDERS_FETCH_FAILED,
  error: Error;
  folderId: TApiFolder['id'] | null;
}

const fetchOrdersFailed = (
  error: Error,
  folderId?: IFetchOrdersFailedAction['folderId']
): IFetchOrdersFailedAction => ({
  type: ORDERS_FETCH_FAILED,
  error,
  folderId: folderId ?? null
});

const fetchOrders = (
  params?: {
    folder_id?: number | null;
    limit?: number;
    offset?: number;
  }
) => {
  return async (dispatch: ThunkDispatch<TRootState, void, Action>): Promise<Array<TApiOrder>> => {
    const folderId = params?.folder_id ?? null;

    try {
      dispatch(fetchOrdersStarted(folderId));

      const response = await Api.Order.list({
        limit: 1000,
        params
      });
      const orders = response.results;

      dispatch(fetchOrdersCompleted(orders, folderId));

      return orders;
    } catch (error) {
      if (ErrorHelpers.isError(error)) {
        dispatch(fetchOrdersFailed(error, folderId));
      }

      throw error;
    }
  };
};


export const ORDERS_MOVE_STARTED = 'ORDERS_MOVE_STARTED';
export const ORDERS_MOVE_COMPLETED = 'ORDERS_MOVE_COMPLETED';
export const ORDERS_MOVE_FAILED = 'ORDERS_MOVE_FAILED';

const moveOrderStarted = (
  order: TApiOrder,
  srcFolderId: TApiFolder['id'] | null,
  targetFolderId: TApiFolder['id'] | null
) => ({
  type: ORDERS_MOVE_STARTED,
  order,
  srcFolderId,
  targetFolderId
});

export interface IMoveOrderRoFolderCompletedAction {
  type: typeof ORDERS_MOVE_COMPLETED,
  order: TApiOrder,
  srcFolderId: number | null;
  targetFolderId: number | null;
}

const moveOrderCompleted = (order: TApiOrder, srcFolderId: number | null, targetFolderId: number | null): IMoveOrderRoFolderCompletedAction => ({
  type: ORDERS_MOVE_COMPLETED,
  order,
  srcFolderId,
  targetFolderId
});

const moveOrderFailed = (error: Error, order: TApiOrder) => ({
  type: ORDERS_MOVE_FAILED,
  error,
  order
});

const moveOrderToFolder = (order: TApiOrder, srcFolderId: TApiFolder['id'] | null, targetFolderId: TApiFolder['id'] | null) => {
  return async (dispatch: ThunkDispatch<TRootState, void, Action>): Promise<TApiOrder> => {
    try {
      dispatch(moveOrderStarted(order, srcFolderId, targetFolderId));

      const updatedOrder = await Api.Order.moveToFolder(order.id, targetFolderId);

      dispatch(moveOrderCompleted(order, srcFolderId, targetFolderId));

      return updatedOrder;
    } catch (error) {
      if (ErrorHelpers.isError(error)) {
        dispatch(moveOrderFailed(error, order));
      }

      throw error;
    }
  };
};

export const ORDER_CREATE_STARTED = 'ORDER_CREATE_STARTED';
export const ORDER_CREATE_COMPLETED = 'ORDER_CREATE_COMPLETED';
export const ORDER_CREATE_FAILED = 'ORDER_CREATE_FAILED';

export interface ICreateOrderCompletedAction {
  type: typeof ORDER_CREATE_COMPLETED,
  order: TApiOrderDetails,
  folderId?: Nullable<number>;
}

const createOrderCompleted = (order: TApiOrderDetails, folderId?: Nullable<number>): ICreateOrderCompletedAction => ({
  type: ORDER_CREATE_COMPLETED,
  order,
  folderId
});

const createOrder = (params: TOrderCreateParams) => {
  return async (dispatch: ThunkDispatch<TRootState, void, ICreateOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const createdOrder = await Api.Order.create(params);

      dispatch(createOrderCompleted(createdOrder, params.folder_id))

      return createdOrder;
    } catch (error) {
      throw error;
    }
  };
};


export const ORDER_DETAILS_FETCH_STARTED = 'ORDER_DETAILS_FETCH_STARTED';
export const ORDER_DETAILS_FETCH_COMPLETED = 'ORDER_DETAILS_FETCH_COMPLETED';
export const ORDER_DETAILS_FETCH_FAILED = 'ORDER_DETAILS_FETCH_FAILED';

interface IFetchOrderDetailsStartedActionOptions {
  silentUpdate: boolean
}

export interface IFetchOrderDetailsStartedAction {
  type: typeof ORDER_DETAILS_FETCH_STARTED,
  id: string | number,
  options: IFetchOrderDetailsStartedActionOptions
}

const fetchOrderDetailsStarted = (
  id: string | number,
  options: IFetchOrderDetailsStartedActionOptions
): IFetchOrderDetailsStartedAction => ({
  type: ORDER_DETAILS_FETCH_STARTED,
  id,
  options
});

export interface IFetchOrderDetailsCompletedAction {
  type: typeof ORDER_DETAILS_FETCH_COMPLETED,
  data: TApiOrderDetails
}

const fetchOrderDetailsCompleted = (data: TApiOrderDetails): IFetchOrderDetailsCompletedAction => ({
  type: ORDER_DETAILS_FETCH_COMPLETED,
  data
});

export interface IFetchOrderDetailsFailedAction {
  type: typeof ORDER_DETAILS_FETCH_FAILED,
  error: AxiosError;
}


const fetchOrderDetails = (id: number | string, silentUpdate = false) => {
  return async (dispatch: ThunkDispatch<TRootState, void, Action>): Promise<TApiOrderDetails> => {
    try {
      dispatch(fetchOrderDetailsStarted(id, { silentUpdate }));

      const order = await Api.Order.fetch(id);

      dispatch(fetchOrderDetailsCompleted(order));

      return order;
    } catch (error) {
      if (ErrorHelpers.isError(error)) {
        dispatch(fetchOrdersFailed(error));
      }

      throw error;
    }
  };
}


export const ORDER_UPDATE_STARTED = 'ORDER_UPDATE_STARTED';
export const ORDER_UPDATE_COMPLETED = 'ORDER_UPDATE_COMPLETED';
export const ORDER_UPDATE_FAILED = 'ORDER_UPDATE_FAILED';

export interface IUpdateOrderCompletedAction {
  type: typeof ORDER_UPDATE_COMPLETED;
  order: TApiOrderDetails;
  folderId?: Nullable<number>;
}

const updateOrderCompleted = (order: TApiOrderDetails, folderId?: Nullable<number>): IUpdateOrderCompletedAction => ({
  type: ORDER_UPDATE_COMPLETED,
  order,
  folderId
});


const updateOrder = (id: number | string, params: TOrderCreateParams) => {
  return async (dispatch: ThunkDispatch<TRootState, void, IUpdateOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const updatedOrder = await Api.Order.update(id, params);

      dispatch(updateOrderCompleted(updatedOrder, params.folder_id));

      return updatedOrder;
    } catch (error) {
      throw error;
    }
  }
}

const partialUpdateOrder = (id: number | string, params: TOrderUpdateParams) => {
  return async (dispatch: ThunkDispatch<TRootState, void, IUpdateOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const updatedOrder = await Api.Order.patch(id, params);

      dispatch(updateOrderCompleted(updatedOrder, params.folder_id));

      return updatedOrder;
    } catch (error) {
      throw error;
    }
  }
}

export const ORDER_REMOVE_STARTED = 'ORDER_REMOVE_STARTED';
export const ORDER_REMOVE_COMPLETED = 'ORDER_REMOVE_COMPLETED';
export const ORDER_REMOVE_FAILED = 'ORDER_REMOVE_FAILED';

export interface IRemoveOrderCompletedAction {
  type: typeof ORDER_REMOVE_COMPLETED;
  orderId: number | string;
  folderId?: Nullable<number>;
}

const removeOrderCompleted = (orderId: number | string, folderId?: Nullable<number>): IRemoveOrderCompletedAction => ({
  type: ORDER_REMOVE_COMPLETED,
  orderId,
  folderId
})

const removeOrder = (id: number | string, folderId?: Nullable<number>) => {
  return async (dispatch: ThunkDispatch<TRootState, void, IRemoveOrderCompletedAction>): Promise<void> => {
    try {
      await Api.Order.remove(id);

      dispatch(removeOrderCompleted(id, folderId));
    } catch (error) {
      throw error;
    }
  }
}

export const ORDER_SUBMIT_COMPLETED: 'ORDER_SUBMIT_COMPLETED' = 'ORDER_SUBMIT_COMPLETED';

export interface ISubmitOrderCompletedAction {
  type: typeof ORDER_SUBMIT_COMPLETED;
  order: TApiOrderDetails;
  folderId?: Nullable<number>;
}

const submitOrderCompleted = (order: TApiOrderDetails, folderId?: Nullable<number>) => ({
  type: ORDER_SUBMIT_COMPLETED,
  order,
  folderId
})

const submitOrder = (order: TApiOrderDetails, params: TOrderSubmitParams, folderId?: Nullable<number>) => {
  return async (dispatch: ThunkDispatch<TRootState, void, ISubmitOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const submittedOrder = await Api.Order.submit(order.id, params);

      dispatch(submitOrderCompleted(submittedOrder, folderId));

      return submittedOrder;
    } catch (error) {
      throw error;
    }
  }
}

const duplicateOrder = (order: TApiOrder, folderId?: Nullable<number>) => {
  return async (dispatch: ThunkDispatch<TRootState, void, ICreateOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const createdOrder = await Api.Order.duplicate(order.id, folderId);

      dispatch(createOrderCompleted(createdOrder, folderId));

      return createdOrder;
    } catch (error) {
      throw new Error('Could not duplicate the order.');
    }
  }
}

export const ORDER_CANCEL_COMPLETED: 'ORDER_CANCEL_COMPLETED' = 'ORDER_CANCEL_COMPLETED';

export interface ICancelOrderCompletedAction {
  type: typeof ORDER_CANCEL_COMPLETED;
  order: TApiOrderDetails;
  folderId?: Nullable<number>;
}

const cancelOrderCompleted = (order: TApiOrderDetails, folderId?: Nullable<number>) => ({
  type: ORDER_CANCEL_COMPLETED,
  order,
  folderId
})

const cancelOrder = (order: TApiOrder, folderId?: Nullable<number>) => {
  return async (dispatch: ThunkDispatch<TRootState, void, ICancelOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const cancelledOrder = await Api.Order.cancel(order.id);

      dispatch(cancelOrderCompleted(cancelledOrder, folderId));

      return cancelledOrder;
    } catch (error) {
      throw error;
    }
  }
}

export const ORDER_APPROVE_COMPLETED: 'ORDER_APPROVE_COMPLETED' = 'ORDER_APPROVE_COMPLETED';

export interface IApproveOrderCompletedAction {
  type: typeof ORDER_APPROVE_COMPLETED;
  order: TApiOrderDetails;
  folderId?: Nullable<number>;
}

const approveOrderCompleted = (order: TApiOrderDetails, folderId?: Nullable<number>) => ({
  type: ORDER_APPROVE_COMPLETED,
  order,
  folderId
})

const approveOrder = (order: TApiOrder, folderId?: Nullable<number>) => {
  return async (dispatch: ThunkDispatch<TRootState, void, IApproveOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const approvedOrder = await Api.Order.approve(order.id);

      dispatch(approveOrderCompleted(approvedOrder, folderId));

      return approvedOrder;
    } catch (error) {
      throw error;
    }
  }
}

export const ORDER_REJECT_COMPLETED: 'ORDER_REJECT_COMPLETED' = 'ORDER_REJECT_COMPLETED';

export interface IRejectOrderCompletedAction {
  type: typeof ORDER_REJECT_COMPLETED;
  order: TApiOrderDetails;
  folderId?: Nullable<number>;
}

const rejectOrderCompleted = (order: TApiOrderDetails, folderId?: Nullable<number>) => ({
  type: ORDER_REJECT_COMPLETED,
  order,
  folderId
})

const rejectOrder = (order: TApiOrder, folderId?: Nullable<number>) => {
  return async (dispatch: ThunkDispatch<TRootState, void, IRejectOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const rejectedOrder = await Api.Order.reject(order.id, '');

      dispatch(rejectOrderCompleted(rejectedOrder, folderId));

      return rejectedOrder;
    } catch (error) {
      throw error;
    }
  }
}

export const ORDER_RECALCULATE_COMPLETED = 'ORDER_RECALCULATE_COMPLETED' as const;

export interface IRecalculateOrderCompletedAction {
  type: typeof ORDER_RECALCULATE_COMPLETED;
  order: TApiOrderDetails;
}

const recalculateOrderCompleted = (order: TApiOrderDetails) => ({
  type: ORDER_RECALCULATE_COMPLETED,
  order
})

const recalculateOrder = (order: TApiOrder) => {
  return async (dispatch: ThunkDispatch<TRootState, void, IRecalculateOrderCompletedAction>): Promise<TApiOrderDetails> => {
    try {
      const updatedOrder = await Api.Order.recalculate(order.id);

      dispatch(recalculateOrderCompleted(updatedOrder));

      return updatedOrder
    } catch (error) {
      throw error;
    }
  }
}

export {
  createOrder,
  updateOrder,
  partialUpdateOrder,
  moveOrderToFolder,
  fetchOrders,
  fetchOrderDetails,
  removeOrder,
  submitOrder,
  cancelOrder,
  approveOrder,
  rejectOrder,
  recalculateOrder,
  duplicateOrder
};
